import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../services/api';
import { useAuthStore } from '../../stores/authStore.js';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useTrusteesFooter, renderTrusteesHtml } from '../../components/reportTrusteesFooter';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '0';
  return new Intl.NumberFormat('id-ID').format(value);
};

function Pinjaman() {
  const { user } = useAuthStore();
  const [pinjamanData, setPinjamanData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startDay, setStartDay] = useState('1');
  const [startMonth, setStartMonth] = useState('');
  const [startYear, setStartYear] = useState('');
  const [endDay, setEndDay] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [endYear, setEndYear] = useState('');
  const [coopProfile, setCoopProfile] = useState(null);
  const [branchInfo, setBranchInfo] = useState(null);
  const [logoBase64, setLogoBase64] = useState('');

  const { trustees } = useTrusteesFooter(startDate, endDate);

  useEffect(() => {
    const loadLogo = async () => {
      try {
        const response = await fetch('/logo.jpeg');
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => setLogoBase64(reader.result);
        reader.readAsDataURL(blob);
      } catch (e) { console.log('Failed to load logo') }
    }
    loadLogo()
  }, []);

  useEffect(() => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    setStartYear(String(y));
    setStartMonth('01');
    setStartDay('01');
    setEndYear(String(y));
    setEndMonth(m);
    setEndDay(d);

    const fetchProfileInfo = async () => {
      try {
        const response = await api.get('/reports/profile-info');
        setCoopProfile(response.data?.data?.coopProfile);
        setBranchInfo(response.data?.data?.branchInfo);
      } catch (error) {
        console.error('Error fetching profile info:', error);
      }
    };
    fetchProfileInfo();
  }, []);

  // Compose date strings from dropdown parts
  useEffect(() => {
    if (startYear && startMonth && startDay) {
      setStartDate(`${startYear}-${startMonth}-${startDay}`);
    }
  }, [startYear, startMonth, startDay]);

  useEffect(() => {
    if (endYear && endMonth && endDay) {
      setEndDate(`${endYear}-${endMonth}-${endDay}`);
    }
  }, [endYear, endMonth, endDay]);

  const fetchPinjamanData = useCallback(async () => {
    if (!startDate || !endDate) return;
    if (new Date(startDate) > new Date(endDate)) return;

    setLoading(true);
    try {
      const response = await api.get('/reports/pinjaman', { params: { startDate, endDate } });
      setPinjamanData(response.data?.data);
    } catch (error) {
      console.error('Error fetching pinjaman data:', error);
      setPinjamanData(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchPinjamanData();
  }, [fetchPinjamanData]);

  const handlePrint = () => {
    if (!pinjamanData) return;
    const printWindow = window.open('', '_blank');
    const htmlContent = `
      <html>
        <head>
          <title>Cetak Laporan Pinjaman</title>
          <style>
            body { font-family: sans-serif; padding: 30px; font-size: 11px; }
            .kop-surat { text-align: center; border-bottom: 3px solid #000; padding-bottom: 15px; margin-bottom: 20px; }
            .kop-surat-inner { display: flex; align-items: center; gap: 15px; }
            .kop-logo { width: 70px; height: auto; }
            .kop-text { text-align: center; flex: 1; }
            .kop-nama { font-size: 20px; font-weight: bold; margin: 5px 0; }
            .kop-badan-hukum { font-size: 12px; margin: 3px 0; }
            .kop-alamat { font-size: 11px; margin: 2px 0; }
            .kop-kontak { font-size: 10px; margin: 2px 0; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
            th, td { border: 1px solid #ccc; padding: 6px; }
            .text-right { text-align: right; }
            .bg-gray { background: #f2f2f2; font-weight: bold; }
            .section-header { background: #e8f4f8; font-weight: bold; padding: 5px; border: 1px solid #ccc; margin-bottom: 5px; }
          </style>
        </head>
        <body>
          <div class="kop-surat">
            <div class="kop-surat-inner">
              <img src="${window.location.origin}/logo.jpeg" class="kop-logo" alt="Logo" />
              <div class="kop-text">
                <div class="kop-nama">${coopProfile?.NamaKoperasi || 'KOPERASI SERBA USAHA'}</div>
                <div class="kop-badan-hukum">Badan Hukum No. : ${coopProfile?.NoBadanHukum || '-'}</div>
                <div class="kop-alamat">${coopProfile?.Alamat || ''}</div>
                <div class="kop-kontak">Telp: ${coopProfile?.Telepon || '-'}  |  Email: ${coopProfile?.Email || '-'}  |  Website: ${coopProfile?.Website || '-'}  |  NPWP: ${coopProfile?.NPWP || '-'}</div>
              </div>
            </div>
          </div>
          <h5 style="text-align: center; margin: 15px 0;">LAPORAN PINJAMAN</h5>
          <p style="text-align: center; font-size: 10px;">Periode: ${formatDate(startDate)} s/d ${formatDate(endDate)}</p>
          ${(pinjamanData.result || []).map(group => renderGroupHTML(group)).join('')}
          <table>
            <tr class="bg-gray">
              <td colspan="6">TOTAL PINJAMAN</td>
              <td class="text-right">${formatCurrency(pinjamanData.totals?.TotalPinjaman)}</td>
              <td class="text-right">${formatCurrency(pinjamanData.totals?.TotalSaldoAkhir)}</td>
            </tr>
          </table>
        ${renderTrusteesHtml(trustees)}
        </body>
      </html>
    `;
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
  };

  const renderGroupHTML = (group) => `
    <div class="section-header">${group.NamaJenisPinjaman}</div>
    <table>
      <thead><tr><th>Tanggal</th><th>No Pinjaman</th><th>Nama Anggota</th><th>Jangka Waktu</th><th class="text-right">Bunga</th><th class="text-right">Jumlah Pinjaman</th><th class="text-right">Jumlah Dicairkan</th><th class="text-right">Sisa Pokok</th></tr></thead>
      <tbody>
        ${(group.transactions || []).map(t => `<tr><td>${formatDate(t.TglPinjaman)}</td><td>${t.NoPinjaman}</td><td>${t.Nama}</td><td>${t.JangkaWaktu}</td><td class="text-right">${t.Bunga}%</td><td class="text-right">${formatCurrency(t.JumlahPinjaman)}</td><td class="text-right">${formatCurrency(t.JumlahDicairkan)}</td><td class="text-right">${formatCurrency(t.SisaPokok)}</td></tr>`).join('')}
        <tr class="bg-gray"><td colspan="6">SUBTOTAL ${group.NamaJenisPinjaman}</td><td class="text-right">${formatCurrency(group.TotalPinjaman)}</td><td class="text-right">${formatCurrency(group.TotalSaldoAkhir)}</td></tr>
      </tbody>
    </table>
  `;

  const handleExportExcel = () => {
    if (!pinjamanData) return;
    const wb = XLSX.utils.book_new();
    let wsData = [
      [coopProfile?.NamaKoperasi || 'Koperasi'],
      ['LAPORAN PINJAMAN'],
      [`Badan Hukum No. : ${coopProfile?.NoBadanHukum || '-'}`],
      [coopProfile?.Alamat || ''],
      [`Telp: ${coopProfile?.Telepon || '-'}  |  Email: ${coopProfile?.Email || '-'}  |  Website: ${coopProfile?.Website || '-'}  |  NPWP: ${coopProfile?.NPWP || '-'}`],
      [],
      [`Periode: ${formatDate(startDate)} - ${formatDate(endDate)}`],
      [],
      ['', 'TANGGAL', 'NO PINJAMAN', 'NAMA ANGGOTA', 'JANGKA WAKTU', 'BUNGA', 'JUMLAH PINJAMAN', 'JUMLAH DICAIRKAN', 'SISA POKOK']
    ];

    (pinjamanData.result || []).forEach(group => {
      wsData.push([group.NamaJenisPinjaman]);
      (group.transactions || []).forEach(t => {
        wsData.push(['', formatDate(t.TglPinjaman), t.NoPinjaman, t.Nama, t.JangkaWaktu, t.Bunga, t.JumlahPinjaman, t.JumlahDicairkan, t.SisaPokok]);
      });
      wsData.push(['', '', '', '', '', '', 'SUBTOTAL', group.TotalPinjaman, group.TotalSaldoAkhir], []);
    });

    wsData.push(['', '', '', '', '', '', 'TOTAL PINJAMAN', pinjamanData.totals?.TotalPinjaman, pinjamanData.totals?.TotalSaldoAkhir]);

    if (trustees.filter(t => t.Kelompok === 'Pengurus').length) {
      wsData.push([]);
      wsData.push(['PENGURUS:']);
      trustees.filter(t => t.Kelompok === 'Pengurus').forEach(t => {
        wsData.push([t.Jabatan + ' : ' + t.NamaLengkap]);
      });
    }
    if (trustees.filter(t => t.Kelompok === 'Pengawas').length) {
      wsData.push([]);
      wsData.push(['PENGAWAS:']);
      trustees.filter(t => t.Kelompok === 'Pengawas').forEach(t => {
        wsData.push([t.Jabatan + ' : ' + t.NamaLengkap]);
      });
    }

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, 'Pinjaman');
    XLSX.writeFile(wb, `Pinjaman_${startDate}.xlsx`);
  };

  const handleExportPDF = () => {
    if (!pinjamanData) return;
    const doc = new jsPDF({ orientation: 'l' });
    const pageWidth = doc.internal.pageSize.width;

    try {
      doc.addImage(logoBase64, 'JPEG', 14, 5, 20, 20);
    } catch (e) { console.log('Logo PDF failed:', e.message) }

    // Kop Surat Header
    doc.setFontSize(16).setFont(undefined, 'bold')
    doc.text(coopProfile?.NamaKoperasi || 'KOPERASI SERBA USAHA', pageWidth / 2, 10, { align: 'center' })
    doc.setFontSize(9).setFont(undefined, 'normal')
    doc.text(`Badan Hukum No. : ${coopProfile?.NoBadanHukum || '-'}`, pageWidth / 2, 15, { align: 'center' })
    doc.setFontSize(8).text(coopProfile?.Alamat || '', pageWidth / 2, 20, { align: 'center' })
    doc.text(`Telp: ${coopProfile?.Telepon || '-'}  |  Email: ${coopProfile?.Email || '-'}  |  Website: ${coopProfile?.Website || '-'}  |  NPWP: ${coopProfile?.NPWP || '-'}`, pageWidth / 2, 25, { align: 'center' })

    // Garis pemisah
    doc.setLineWidth(0.5)
    doc.line(10, 28, pageWidth - 10, 28)
    doc.setLineWidth(0.3)
    doc.line(10, 29, pageWidth - 10, 29)

    // Judul Laporan
    doc.setFontSize(11).setFont(undefined, 'bold')
    doc.text('LAPORAN PINJAMAN', pageWidth / 2, 35, { align: 'center' })
    doc.setFontSize(8).setFont(undefined, 'normal')
    doc.text(`Periode: ${formatDate(startDate)} s/d ${formatDate(endDate)}`, pageWidth / 2, 40, { align: 'center' })

    let yPos = 45;

    (pinjamanData.result || []).forEach(group => {
      doc.setFontSize(10).setFont(undefined, 'bold').text(group.NamaJenisPinjaman, 14, yPos);

      autoTable(doc, {
        startY: yPos + 2,
        head: [['Tanggal', 'No Pinjaman', 'Nama Anggota', 'Jangka Waktu', 'Bunga', 'Jumlah Pinjaman', 'Jumlah Dicairkan', 'Sisa Pokok']],
        body: [
          ...(group.transactions || []).map(t => [formatDate(t.TglPinjaman), t.NoPinjaman, t.Nama, t.JangkaWaktu, `${t.Bunga}%`, formatCurrency(t.JumlahPinjaman), formatCurrency(t.JumlahDicairkan), formatCurrency(t.SisaPokok)]),
          [{ content: `SUBTOTAL ${group.NamaJenisPinjaman}`, colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } }, { content: formatCurrency(group.TotalPinjaman), styles: { halign: 'right' } }, { content: formatCurrency(group.TotalSaldoAkhir), styles: { halign: 'right' } }]
        ],
        theme: 'grid', styles: { fontSize: 7 }
      });
      yPos = doc.lastAutoTable.finalY + 5;
    });

    doc.setFont(undefined, 'bold')
    doc.text(`TOTAL PINJAMAN: ${formatCurrency(pinjamanData.totals?.TotalPinjaman)}`, 14, yPos + 5);
    doc.text(`TOTAL SALDO AKHIR: ${formatCurrency(pinjamanData.totals?.TotalSaldoAkhir)}`, 14, yPos + 10);

    if (trustees.length > 0) {
      doc.setFontSize(6)
      const midX = pageWidth / 2
      
      const pengurus = trustees.filter(t => t.Kelompok === 'Pengurus')
      const pengawas = trustees.filter(t => t.Kelompok === 'Pengawas')
      const pengurusSub = pengurus.filter(t => t.Jabatan === 'Anggota/Panitia Kredit')
      const pengurusRest = pengurus.filter(t => t.Jabatan !== 'Anggota/Panitia Kredit')

      let yL = yPos + 20
      if (pengurus.length) {
        doc.setFont(undefined, 'bold')
        doc.text('PENGURUS:', 14, yL)
        yL += 3
        doc.setFont(undefined, 'normal')
        pengurusRest.forEach(t => {
          doc.text(`${t.Jabatan} : ${t.NamaLengkap}`, 19, yL)
          yL += 3
        })
        if (pengurusSub.length) {
          doc.text('Anggota/Panitia Kredit :', 19, yL)
          yL += 3
          pengurusSub.forEach((t, i) => {
            doc.text(`${i + 1}. ${t.NamaLengkap}`, 24, yL)
            yL += 3
          })
        }
      }

      let yR = yPos + 20
      if (pengawas.length) {
        doc.setFont(undefined, 'bold')
        doc.text('PENGAWAS:', midX + 5, yR)
        yR += 3
        doc.setFont(undefined, 'normal')
        pengawas.forEach(t => {
          doc.text(`${t.Jabatan} : ${t.NamaLengkap}`, midX + 10, yR)
          yR += 3
        })
      }
    }

    doc.save(`Pinjaman_${startDate}.pdf`);
  };

  return (
    <div className="container-fluid p-3">
      <div className="card shadow border-0">
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
          <h5 className="m-0 font-weight-bold">Laporan Pinjaman</h5>
          <div className="btn-group">
            <button className="btn btn-sm btn-success" onClick={handleExportExcel} disabled={loading}><i className="fas fa-file-excel mr-1"></i> Excel</button>
            <button className="btn btn-sm btn-danger" onClick={handleExportPDF} disabled={loading}><i className="fas fa-file-pdf mr-1"></i> PDF</button>
            <button className="btn btn-sm btn-light" onClick={handlePrint} disabled={loading}><i className="fas fa-print mr-1"></i> Cetak</button>
          </div>
        </div>

        <div className="card-body">
          <div className="row mb-4 bg-light p-3 rounded">
            <div className="col-md-6">
              <label className="small font-weight-bold">TANGGAL MULAI</label>
              <div className="d-flex">
                <select className="form-control form-control-sm mr-2" style={{ width: '70px' }} value={startDay} onChange={(e) => setStartDay(e.target.value)}>
                  {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={String(i + 1).padStart(2, '0')}>{i + 1}</option>)}
                </select>
                <select className="form-control form-control-sm mr-2" style={{ width: 'auto' }} value={startMonth} onChange={(e) => setStartMonth(e.target.value)}>
                  <option value="01">Januari</option><option value="02">Februari</option><option value="03">Maret</option>
                  <option value="04">April</option><option value="05">Mei</option><option value="06">Juni</option>
                  <option value="07">Juli</option><option value="08">Agustus</option><option value="09">September</option>
                  <option value="10">Oktober</option><option value="11">November</option><option value="12">Desember</option>
                </select>
                <select className="form-control form-control-sm" style={{ width: '100px' }} value={startYear} onChange={(e) => setStartYear(e.target.value)}>
                  {Array.from({ length: 10 }, (_, i) => <option key={i} value={String(new Date().getFullYear() - 5 + i)}>{new Date().getFullYear() - 5 + i}</option>)}
                </select>
              </div>
            </div>
            <div className="col-md-6">
              <label className="small font-weight-bold">TANGGAL AKHIR</label>
              <div className="d-flex">
                <select className="form-control form-control-sm mr-2" style={{ width: '70px' }} value={endDay} onChange={(e) => setEndDay(e.target.value)}>
                  {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={String(i + 1).padStart(2, '0')}>{i + 1}</option>)}
                </select>
                <select className="form-control form-control-sm mr-2" style={{ width: 'auto' }} value={endMonth} onChange={(e) => setEndMonth(e.target.value)}>
                  <option value="01">Januari</option><option value="02">Februari</option><option value="03">Maret</option>
                  <option value="04">April</option><option value="05">Mei</option><option value="06">Juni</option>
                  <option value="07">Juli</option><option value="08">Agustus</option><option value="09">September</option>
                  <option value="10">Oktober</option><option value="11">November</option><option value="12">Desember</option>
                </select>
                <select className="form-control form-control-sm" style={{ width: '100px' }} value={endYear} onChange={(e) => setEndYear(e.target.value)}>
                  {Array.from({ length: 10 }, (_, i) => <option key={i} value={String(new Date().getFullYear() - 5 + i)}>{new Date().getFullYear() - 5 + i}</option>)}
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center my-5"><div className="spinner-border text-primary"></div></div>
          ) : !pinjamanData ? (
            <div className="alert alert-info">Data tidak tersedia untuk periode ini.</div>
          ) : (
            <>
              <div className="alert alert-secondary">
                <span>Total Pinjaman: <b>{formatCurrency(pinjamanData.totals?.TotalPinjaman)}</b> | Total Saldo Akhir: <b>{formatCurrency(pinjamanData.totals?.TotalSaldoAkhir)}</b></span>
              </div>

              <div className="text-center mb-4">
                <h5 className="font-weight-bold">LAPORAN PINJAMAN</h5>
                <p className="text-muted mb-0">Periode: {formatDate(startDate)} s/d {formatDate(endDate)}</p>
              </div>

              {(pinjamanData.result || []).map((group, idx) => (
                <PinjamanSection key={idx} title={group.NamaJenisPinjaman} items={group.transactions} totalPinjaman={group.TotalPinjaman} totalSaldoAkhir={group.TotalSaldoAkhir} />
              ))}

              <div className="mt-3 p-3 bg-dark text-white rounded d-flex justify-content-between">
                <span className="font-weight-bold">TOTAL PINJAMAN PER ${formatDate(endDate)}</span>
                <span className="font-weight-bold text-warning">{formatCurrency(pinjamanData.totals?.TotalPinjaman)}</span>
              </div>
              <div className="mt-2 p-3 bg-secondary text-white rounded d-flex justify-content-between">
                <span className="font-weight-bold">TOTAL SALDO AKHIR</span>
                <span className="font-weight-bold text-warning">{formatCurrency(pinjamanData.totals?.TotalSaldoAkhir)}</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const PinjamanSection = ({ title, items, totalPinjaman, totalSaldoAkhir }) => (
  <div className="mb-4">
    <h6 className="p-2 bg-light border-left border-primary font-weight-bold">{title}</h6>
    <table className="table table-sm table-bordered">
      <thead className="thead-light">
        <tr><th>Tanggal</th><th>No Pinjaman</th><th>Nama Anggota</th><th>Jangka Waktu</th><th className="text-right">Bunga</th><th className="text-right">Jumlah Pinjaman</th><th className="text-right">Jumlah Dicairkan</th><th className="text-right">Sisa Pokok</th></tr>
      </thead>
      <tbody>
        {items?.map((t, idx) => (
          <tr key={idx}><td>{formatDate(t.TglPinjaman)}</td><td>{t.NoPinjaman}</td><td>{t.Nama}</td><td>{t.JangkaWaktu}</td><td className="text-right">{t.Bunga}%</td><td className="text-right">{formatCurrency(t.JumlahPinjaman)}</td><td className="text-right">{formatCurrency(t.JumlahDicairkan)}</td><td className="text-right">{formatCurrency(t.SisaPokok)}</td></tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="table-warning font-weight-bold">
          <td colSpan="6" className="text-right">SUBTOTAL {title}</td>
          <td className="text-right">{formatCurrency(totalPinjaman)}</td>
          <td className="text-right">{formatCurrency(totalSaldoAkhir)}</td>
        </tr>
      </tfoot>
    </table>
  </div>
);

export default Pinjaman;
